import {connect} from 'pwa-helpers';
import {LitElement, html, css} from 'lit';
import {msg} from '@lit/localize';
import {customElement} from 'lit/decorators/custom-element.js';
import {when} from 'lit/directives/when.js';
import {conditional} from '../../../util/conditional';
import store from '../../../store';
import {filterAndJoin} from '../../../util/filterAndJoin';

import {pimText, pimTextDebug} from '../../../util/values';
import {
	selectIsOnesided,
	selectIsSingle,
	selectSystemLumen,
	selectSystemLumenPerPower,
	selectSystemLumenString,
	selectSystemPower,
	selectSystemPowerString,
	selectWeight,
	selectWindsurfacearea,
	selectSpcConfiguration,
	selectRegion
} from '../../../selectors';

@customElement('line-technical-data')
export class LineTechnicalData extends connect(store)(LitElement) {

	static styles = [
		css`
			:host {
				margin: 0;
				display: flex;
				flex-direction: column;
				container-type: inline-size;
			}

			.subheadline {
				font: var(--font-specsheet-print-small-text);
				font-size: 2cqw;
				margin-top: 5cqw;
				color: #939393;
			}
		`]

	constructor() {
		super();
		store.subscribe(() => this.requestUpdate());
	}

	stateChanged(state) {
		this.state = state;
		this.productData = state.productData;
		this.status = state.status;
		this.lang = state.status.lang;
		this.spcConfiguration = selectSpcConfiguration(state);
		this.conditionalVariantValues = state.productData.conditionalVariantValues;
		this.shortNumberProductModel = state.shortNumber.data.ProductModel ?? '';
	}

	t(featureId, context, value, prefix, suffix) {
		return this.debug ?
			pimTextDebug(this.lang, featureId, context, this.spcConfiguration, this.state.productData.translations, value, prefix, suffix) :
			pimText(this.lang, featureId, context, this.spcConfiguration, this.state.productData.translations, value, prefix, suffix);
	}

	c(id) {
		return conditional(this.spcConfiguration, this.conditionalVariantValues, id);
	}


	render() {
		const s = this.state;
		const shouldDisplayFeature = (value) => value !== undefined && value !== '';
		const descAtt = this.t(this.c('DescriptiveAttributeReference.LE_CRI'), 'DV');
		const isDescAttPresent = descAtt !== undefined;

		const applicableStandards = [
			this.t('LU_IP.IP66', 'DV.S'),
			this.t('LU_IK.10', 'DV.S'),
			this.t('EB_ElectricalProtectionClass_1', 'FV.S'),
			this.t('LU_ENEC_CertificateDescription.ENEC', 'DV.S'),
			this.t('LU_CE.CE', 'DV.S'),
		];
		const filteredStandards = applicableStandards.filter(value => value !== undefined && value !== '');
		const isStandardsPresent = filteredStandards.length > 0;

		return html`

			${shouldDisplayFeature(this.t('HS_HousingType', 'FV.S')) ?
				html`
					<shared-specsheet-feature>
						<!-- Modell -->
						<trans-label slot="label" key="HS_HousingType" context="F"></trans-label>
						<trans-label slot="value" key="HS_HousingType" context="FV.S"></trans-label>
					</shared-specsheet-feature>` : ''}

			${shouldDisplayFeature(this.t('LE_ColourTemperature_1', 'FV.S')) ?
				html`
					<shared-specsheet-feature>
						<!-- Lichtfarbe -->
						<trans-label slot="label" key="LE_ColourTemperature_1" context="F"></trans-label>
						<span slot="value">
										<trans-label key="LE_ColourTemperature_1" context="FV.S" prefix="LED "></trans-label><br>
										<trans-label key="LE_ColourTemperature_2" context="FV.S" prefix="LED "></trans-label>
									</span>
					</shared-specsheet-feature>` : ''}


			<shared-specsheet-feature>
				<!-- Systemlumen -->
				<trans-label slot="label" key="LU_SystemLuminousFlux" context="D"></trans-label>
				<span slot="value">
								${selectSystemLumen(s) + ' ' + this.t('LU_SystemLuminousFluxUnit.LM', 'DV.S')}
								${!selectIsOnesided(s) ? '(' + selectSystemLumenString(s) + ' ' + this.t('LU_SystemLuminousFluxUnit.LM', 'DV.S') + ')' : null}
							</span>
			</shared-specsheet-feature>


			${isDescAttPresent ? html`
				<shared-specsheet-feature>
					<trans-label slot="label" class="label" key="LE_CRI" context="D"></trans-label>
					<span slot="value" class="value">${descAtt}</span>
				</shared-specsheet-feature>` : ''}

			<shared-specsheet-feature>
				<!-- Systemleistung -->
				<trans-label slot="label" key="LU_SystemWattage" context="D"></trans-label>
				<span slot="value">
								${selectSystemPower(s)} <trans-label key="LU_SystemWattageUnit.W" context="DV.S"></trans-label>
								${!selectIsOnesided(s) ? '(' + selectSystemPowerString(s) + ' ' + this.t('LU_SystemWattageUnit.W', 'DV.S') + ')' : null}
							</span>
			</shared-specsheet-feature>

			<shared-specsheet-feature>
				<!-- Lichtausbeute -->
				<trans-label slot="label" key="LU_Efficacy" context="D"></trans-label>
				<span slot="value">${selectSystemLumenPerPower(s)} <trans-label key="LU_EfficacyUnit.lm/W"
																				context="DV.S"></trans-label></span>
			</shared-specsheet-feature>


			${when(!selectIsSingle(s),
				() => html`
					<shared-specsheet-feature>
						<!-- Lichtverteilung -->
						<trans-label slot="label" key="HS_Construction" context="F"></trans-label>
						<trans-label slot="value" key="HS_Construction" context="FV.S"></trans-label>
					</shared-specsheet-feature>
				`,
				() => html`
				`)}

			<shared-specsheet-feature>
				<trans-label slot="label" key="LE_LightEngineDescription" context="D"></trans-label>
				<span slot="value">
							${filterAndJoin([
								this.t('LE_LightEngineModuleSize_1', 'FV.S'),
								this.t('LE_LightDistribution_1', 'FV.S'),
								this.t('LE_OpticalVersion_1', 'FV.S'),
								this.t('LE_ReflectorColour_1', 'FV.S'),
							], ', ')}

							${when(!selectIsOnesided(s), () => html`<br>
							${filterAndJoin([
								this.t('LE_LightEngineModuleSize_2', 'FV.S'),
								this.t('LE_LightDistribution_2', 'FV.S'),
								this.t('LE_OpticalVersion_2', 'FV.S'),
								this.t('LE_ReflectorColour_2', 'FV.S'),
							], ', ')}
							`)}
						</span>
			</shared-specsheet-feature>

			${shouldDisplayFeature(this.t('HS_UpLight', 'FV.S')) ?
				html`
					<shared-specsheet-feature>" >
						<trans-label slot="label" key="HS_UpLight" context="F"></trans-label>
						<span slot="value">
							${filterAndJoin([
								this.t('HS_UpLight', 'FV.S'),
								selectRegion(s) === 'fra' ? this.t('HS_LightEmissionCA', 'FV.S') : ''
							], ', ')}
						</span>
					</shared-specsheet-feature>` : ''}

			${shouldDisplayFeature(this.c('EB_EBlockOperatingGearDescription.ElectronicConverter"', 'DV.S')) ?
				html`
					<shared-specsheet-feature>
						<!-- Elektrische Ausführung -->
						<trans-label slot="label" key="EB_EBlockOperatingGearDescription" context="D"></trans-label>
						<trans-label slot="value" key="EB_EBlockOperatingGearDescription.ElectronicConverter"
									 context="DV.S"></trans-label>
					</shared-specsheet-feature>` : ''}


			${shouldDisplayFeature(this.c('LU_SurgeProtection.10 kV"', 'DV.S')) ?
				html`
					<shared-specsheet-feature>
						<!-- Überspannungsschutz -->
						<trans-label slot="label" key="LU_SurgeProtection" context="D"></trans-label>
						<trans-label slot="value" key="LU_SurgeProtection.10 kV" context="DV.S"></trans-label>
					</shared-specsheet-feature>` : ''}


			${shouldDisplayFeature(this.t('EB_Control_1', 'FV.S')) ?
				html`
					<shared-specsheet-feature>
						<!-- Steuerung -->
						<trans-label slot="label" key="EB_Control_1" context="F"></trans-label>
						<trans-label slot="value" key="EB_Control_1" context="FV.S"></trans-label>
					</shared-specsheet-feature>` : ''}


			${shouldDisplayFeature(this.t('EB_Zhaga_1', 'FV.S')) ?
				html`
					<shared-specsheet-feature>
						<!-- Zhaga -->
						<trans-label slot="label" key="EB_Zhaga_1" context="F"></trans-label>
						<trans-label slot="value" key="EB_Zhaga_1" context="FV.S"></trans-label>
					</shared-specsheet-feature>` : ''}


			${shouldDisplayFeature(this.t('EB_ConstantLumenTechnology_1', 'FV.S')) ?
				html`
					<shared-specsheet-feature>
						<!-- CLT -->
						<trans-label slot="label" key="EB_ConstantLumenTechnology_1" context="F"></trans-label>
						<trans-label slot="value" key="EB_ConstantLumenTechnology_1" context="FV.S"></trans-label>
					</shared-specsheet-feature>` : ''}


			${shouldDisplayFeature(this.c('LU_TechnicalRegion.220-240V 50/60 Hz', 'DV.S')) ?
				html`
					<shared-specsheet-feature>
						<!-- Frequenz/Spannung -->
						<trans-label slot="label" key="LU_TechnicalRegion" context="D"></trans-label>
						<trans-label slot="value" key="LU_TechnicalRegion.220-240V 50/60 Hz" context="DV.S"></trans-label>
					</shared-specsheet-feature>` : ''}


			${shouldDisplayFeature(this.t('HS_HousingColour', 'FV.S')) ?
				html`
					<shared-specsheet-feature>
						<!-- Gehäusefarbe -->
						<trans-label slot="label" key="HS_HousingColour" context="F"></trans-label>
						<trans-label slot="value" key="HS_HousingColour" context="FV.S"></trans-label>
					</shared-specsheet-feature>` : ''}


			${this.c('DescriptiveAttributeReference.LU_LuminaireMaterial') ? html`
				<shared-specsheet-feature>
					<!-- Gehäuse -->
					<trans-label slot="label" key="LU_LuminaireMaterial" context="D"></trans-label>
					<trans-label slot="value" .key="${this.c('DescriptiveAttributeReference.LU_LuminaireMaterial')}"
								 context="DV.S"></trans-label>
				</shared-specsheet-feature>
			` : ''}

			${shouldDisplayFeature(this.t('HS_HousingCoating', 'FV.S')) ?
				html`
					<shared-specsheet-feature>
						<!-- Oberfläche -->
						<trans-label slot="label" key="HS_HousingCoating" context="F"></trans-label>
						<trans-label slot="value" key="HS_HousingCoating" context="FV.S"></trans-label>
					</shared-specsheet-feature>` : ''}


			${shouldDisplayFeature(this.t('HS_Base', 'FV.S')) ?
				html`
					<shared-specsheet-feature>
						<!-- Gründung -->
						<trans-label slot="label" key="HS_Base" context="F"></trans-label>
						<trans-label slot="value" key="HS_Base" context="FV.S"></trans-label>
					</shared-specsheet-feature>` : ''}

			<shared-specsheet-feature>
				<trans-label slot="label" key="HS_HousingPowerConnection" context="F"></trans-label>
				<!-- Ausstattung -->
				${when(!selectIsSingle(s),
					() => html`
						<span slot="value">
									<trans-label key="HS_NumberPoleDoors" context="FV.S"></trans-label><br>
									<trans-label key="HS_HousingMountingLocationEblock_1" context="F"></trans-label>: <trans-label
							key="EB_EBlockMountingType_1" context="FV.S"></trans-label>
									${when(!selectIsOnesided(s), () => html`
										<br>
										<trans-label key="HS_HousingMountingLocationEblock_2" context="F"></trans-label>:
										<trans-label key="EB_EBlockMountingType_2" context="FV.S"></trans-label>
									`)}
									<br>
									<trans-label .key="${this.c('DescriptiveAttributeReference.LU_FuseBox')}" context="DV.S"></trans-label>
								</span>
					`,
					() => html`
						<span slot="value">
									<trans-label key="HS_HousingPowerConnection" context="FV.S"></trans-label>
								</span>
					`
				)}
			</shared-specsheet-feature>


			${shouldDisplayFeature(this.t('HS_HousingHeight', 'FV.S')) ?
				html`
					<shared-specsheet-feature>
						<!-- Höhe -->
						<trans-label slot="label" key="HS_HousingHeight" context="F"></trans-label>
						<trans-label slot="value" key="HS_HousingHeight" context="FV.S"></trans-label>
					</shared-specsheet-feature>` : ''}

			${(this.t('HS_HousingDiameter.114', 'DV.S') !== '' || this.t('HS_HousingDiameterUnit.MM', 'DV.S') !== '') ?
				html`
					<shared-specsheet-feature>
						<!-- Durchmesser -->
						<trans-label slot="label" key="HS_HousingDiameterUnit" context="D"></trans-label>
						<span slot="value">
							<trans-label key="HS_HousingDiameter.114" context="DV.S"></trans-label>
							<trans-label key="HS_HousingDiameterUnit.MM" context="DV.S"></trans-label>
						</span>
					</shared-specsheet-feature>
				` : ''}


			${shouldDisplayFeature(this.c('LU_LuminaireWeightUnit.KG', 'DV.S')) ?
				html`
					<shared-specsheet-feature>
						<!-- Gewicht -->
						<trans-label slot="label" key="LU_LuminaireWeight" context="D"></trans-label>
						<span slot="value" class="specsheet__value">
							${selectWeight(s)}
							<trans-label key="LU_LuminaireWeightUnit.KG" context="DV.S"></trans-label><br>
						</span>
					</shared-specsheet-feature>` : ''}

			${shouldDisplayFeature(selectWindsurfacearea(s)) ?
				html`
					<shared-specsheet-feature>
						<!-- Windangriffsfläche -->
						<trans-label slot="label" key="HS_WindSurfaceArea" context="D"></trans-label>
						<span slot="value">
							${selectWindsurfacearea(s)}
							<trans-label key="HS_WindSurfaceAreaUnit.M2" context="DV.S"></trans-label>
						</span>
					</shared-specsheet-feature>` : ''}

			${isStandardsPresent ? html`
				<shared-specsheet-feature>
					<trans-label slot="label" class="label" key="LU_ApplicableStandards" context="D"></trans-label>
					<span slot="value" class="value">
							${filterAndJoin(filteredStandards, ', ')}
					</span>
				</shared-specsheet-feature>` : ''}

			${this.shortNumberProductModel ? html`
				<shared-specsheet-feature>
					<span slot="label" class="label">${msg('Model ID')}</span>
					<span slot="value" class="value">
							${this.shortNumberProductModel}
					</span>
				</shared-specsheet-feature>` : ''}


        `;
	}
}
